import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const SessionMonitorHeader = ({ sidebarCollapsed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeSessions] = useState([
    { id: 1, clientName: 'Margaret Chen', exercise: 'Seated Leg Extensions', duration: '12:34', status: 'active' },
    { id: 2, clientName: 'Robert Williams', exercise: 'Arm Circles', duration: '08:22', status: 'active' },
    { id: 3, clientName: 'Dorothy Martinez', exercise: 'Balance Training', duration: '15:45', status: 'paused' }
  ]);
  const [showSessionDropdown, setShowSessionDropdown] = useState(false);
  const [alerts] = useState([
    { id: 1, type: 'warning', message: 'Margaret Chen - Heart rate elevated', priority: 'high' },
    { id: 2, type: 'info', message: 'Robert Williams - Session ending in 5 minutes', priority: 'medium' }
  ]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getPageTitle = () => {
    const pathTitles = {
      '/trainer-dashboard': 'Trainer Dashboard',
      '/client-management': 'Client Management',
      '/exercise-database': 'Exercise Database',
      '/training-plan-builder': 'Training Plan Builder'
    };
    return pathTitles?.[location?.pathname] || 'FitTrainer Pro';
  };

  const handleSessionClick = (sessionId) => {
    navigate(`/exercise-execution?session=${sessionId}&monitor=true`);
    setShowSessionDropdown(false);
  };

  const handleEmergencyStop = () => {
    // Emergency stop functionality
    console.log('Emergency stop activated for all sessions');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'paused': return 'text-warning';
      case 'completed': return 'text-muted-foreground';
      default: return 'text-foreground';
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'warning': return 'AlertTriangle';
      case 'error': return 'AlertCircle';
      case 'info': return 'Info';
      default: return 'Bell';
    }
  };

  const getAlertColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-muted-foreground';
      default: return 'text-foreground';
    }
  };

  return (
    <header 
      className={`
        fixed top-0 right-0 z-40 h-55 bg-surface border-b border-border
        transition-all duration-300 ease-out
        ${sidebarCollapsed ? 'left-20' : 'left-80'}
        lg:${sidebarCollapsed ? 'left-20' : 'left-80'}
        md:${sidebarCollapsed ? 'left-16' : 'left-72'}
        sm:${sidebarCollapsed ? 'left-16' : 'left-64'}
      `}
      role="banner"
    >
      <div className="flex items-center justify-between h-full px-6">
        {/* Page Title and Time */}
        <div className="flex items-center space-x-6">
          <div>
            <h1 className="text-lg font-semibold text-foreground">{getPageTitle()}</h1>
            <p className="text-sm text-muted-foreground">
              {currentTime?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} • 
              {currentTime?.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Session Monitoring and Controls */}
        <div className="flex items-center space-x-4">
          {/* Active Sessions Indicator */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSessionDropdown(!showSessionDropdown)}
              className="breathing-focus"
              aria-label="View active sessions"
            >
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-success rounded-full animate-gentle-pulse"></div>
                <span className="font-medium">{activeSessions?.length}</span>
                <span className="text-sm text-muted-foreground hidden sm:inline">Active</span>
                <Icon name="ChevronDown" size={16} />
              </div>
            </Button>

            {/* Sessions Dropdown */}
            {showSessionDropdown && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-popover border border-border rounded-lg shadow-lg z-50">
                <div className="p-4 border-b border-border">
                  <h3 className="font-semibold text-foreground">Active Sessions</h3>
                  <p className="text-sm text-muted-foreground">Real-time monitoring</p>
                </div>
                
                <div className="max-h-64 overflow-y-auto">
                  {activeSessions?.map((session) => (
                    <button
                      key={session?.id}
                      onClick={() => handleSessionClick(session?.id)}
                      className="w-full p-4 text-left hover:bg-accent transition-colors duration-200 border-b border-border last:border-b-0"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-foreground">{session?.clientName}</p>
                          <p className="text-sm text-muted-foreground">{session?.exercise}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-mono">{session?.duration}</p>
                          <p className={`text-xs capitalize ${getStatusColor(session?.status)}`}>
                            {session?.status}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                
                <div className="p-4 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      navigate('/trainer-dashboard');
                      setShowSessionDropdown(false);
                    }}
                  >
                    <Icon name="LayoutDashboard" size={16} className="mr-2" />
                    View Dashboard
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Alerts */}
          {alerts?.length > 0 && (
            <div className="relative">
              <Button
                variant="outline"
                size="icon"
                className="breathing-focus relative"
                aria-label={`${alerts?.length} alerts`}
              >
                <Icon name="Bell" size={20} />
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-error-foreground text-xs rounded-full flex items-center justify-center">
                  {alerts?.length}
                </span>
              </Button>
            </div>
          )}

          {/* Emergency Stop */}
          <Button
            variant="destructive"
            size="sm"
            onClick={handleEmergencyStop}
            className="breathing-focus haptic-button"
            aria-label="Emergency stop all sessions"
          >
            <Icon name="Square" size={16} className="mr-2" />
            <span className="hidden sm:inline">Emergency Stop</span>
            <span className="sm:hidden">Stop</span>
          </Button>

          {/* Profile Menu */}
          <div className="flex items-center space-x-3 pl-4 border-l border-border">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              <Icon name="User" size={16} color="white" />
            </div>
            <div className="hidden md:block">
              <p className="text-sm font-medium text-foreground">Dr. Sarah Johnson</p>
              <p className="text-xs text-muted-foreground">Senior Trainer</p>
            </div>
            <Button variant="ghost" size="icon" className="breathing-focus">
              <Icon name="Settings" size={16} />
            </Button>
          </div>
        </div>
      </div>
      {/* Alert Banner */}
      {alerts?.some(alert => alert?.priority === 'high') && (
        <div className="  border-error/20 px-6 py-2">
          <div className="flex  space-x-2 ">
            <Icon name="AlertTriangle" size={16} className="text-error mt-2" />
            <p className="text-sm text-error font-medium mt-2">
              High priority alerts require attention
            </p>
            <Button variant="ghost"  className=" text-error mt-4 py-0  h-0" >
              <span className="">View All</span>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default SessionMonitorHeader;